import React, { useEffect, useState } from 'react';
import { bool, func, object } from 'prop-types';
import { Drawer, Tabs, notification, Divider } from 'antd';
import { Field, Formik, Form } from 'formik';
import moment from 'moment';
import { sortBy, get, orderBy, map, toNumber, omit, includes, filter, isEmpty, head, some } from 'lodash';
import Questions from './QuestionsForm/Questions';
import AddQuestionButton from './QuestionsForm/AddQuestionButton';
import { ConfirmFooterButtons, FormikInput, FormikSelect, FormikDatePicker, Column, Text, CompanyGroupsSelect, Row, Icon } from '../../../common';
import { monthOptions, disabledShippingDates, validateQuestions, formatMonthEvaluate, formatActivationRange, formatStores } from '../../../../utils/misc';
import CREATE_POLL from '../../../../queries/CREATE_POLL';
import UPDATE_POLL from '../../../../queries/UPDATE_POLL';

const { TabPane } = Tabs;

const styleNotification = {
    borderRadius: 8,
    height: 42,
    padding: '9px 12px',
    top: 45
};

const DrawerPoll = ({ visible, onClose, pollSelected, isCreate, session, client, refreshData }) => {
    const sessionSalesChannel = orderBy(map(get(session, 'salesChannels'), ({ name, store, id, kind }) => ({ label: `${store} - ${name}`, value: id, kind })), ['label'], ['asc']);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [selectedCompanyGroup, onChangeSelectedCompanyGroup] = useState({});
    const defaultFlag = get(head(filter(sessionSalesChannel, (store) => includes(map(get(pollSelected, 'associatedStore'), 'idStore'), toNumber(get(store, 'value'))))), 'kind');

    const handleSelectedCompanyGroup = (value, setFieldValue) => {
        onChangeSelectedCompanyGroup(value);
        defaultFlag !== value && setFieldValue('associatedStore', []);
    };

    const showNotification = (isSuccess) => {
        setTimeout(() => onClose(), 1000);

        const error = {
            msg: `Hubo un error al ${isCreate ? 'crear' : 'editar'} encuesta.`,
            color: '#BD2C00',
            icon: 'alert-circle',
            bgColor: '#F7E6E1',
            borderColor: '#BD2C00'
        };

        const success = {
            msg: `La encuesta se ${isCreate ? 'creó' : 'editó'} correctamente.`,
            color: '#22A889',
            icon: 'checkmark-circle',
            bgColor: '#E6F5F1',
            borderColor: '#B6DFD6'
        };

        const { msg, bgColor, icon, color, borderColor } = isSuccess ? success : error;

        notification.open({
            message: (<Row align='center'>
                <Icon name={icon} size={24} color={color} />
                <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
                <Text text={msg} size={12} color='#323A44' />
            </Row>),
            style: { ...styleNotification, backgroundColor: bgColor, border: `1px solid ${borderColor}` }
        });
    };

    const initialValues = () => {
        const questions = get(pollSelected, 'questions');
        const activationRange = head(get(pollSelected, 'activationRange'));
        const init = get(activationRange, 'init');
        const end = get(activationRange, 'end');
        const associatedStore = get(pollSelected, 'associatedStore')
        const storeIds = map(associatedStore, 'idStore');
        const stores = filter(sessionSalesChannel, (store) => includes(storeIds, toNumber(get(store, 'value'))));

        return {
            ...pollSelected,
            questions: sortBy(questions, 'orden'),
            associatedStore: stores,
            activationRange: isEmpty(activationRange) ? [moment(), moment()] : [moment(init), moment(end)],
            monthsEvaluate: filter(monthOptions, ({ value }) => some(get(pollSelected, 'monthsEvaluate'), { months_evaluate: value }))
        };
    };

    useEffect(() => {
        setLoading(false);
        setSuccess(false);
    }, []);

    const submit = async (values) => {
        const formattedValues = {
            ...omit(values, 'numberReponses'),
            associatedStore: formatStores(get(values, 'associatedStore'), get(pollSelected, 'associatedStore'), sessionSalesChannel),
            activationRange: formatActivationRange(get(values, 'activationRange')),
            monthsEvaluate: formatMonthEvaluate(get(values, 'monthsEvaluate')),
            userCreate: get(session, 'email'),
            questions: validateQuestions(get(values, 'questions')),
            status: isCreate ? 2 : get(values, 'status'),
            userUpdate: get(session, 'email')
        };

        setLoading(true);
        const response = await client.mutate({
            mutation: isCreate ? CREATE_POLL : UPDATE_POLL,
            variables: {
                input: {
                    ...omit(formattedValues, ['dateCreate', 'dateUpdate', '__typename'])
                }
            },
            refetchQueries: ['getPolls']
        });

        const action = isCreate ? 'createPoll' : 'updatePoll';
        const isSuccess = get(response, `data.${action}.internalCode`) === '200';
        setSuccess(isSuccess);
        showNotification(isSuccess);
        setLoading(false);
        refreshData();
    };

    const salesChannelFilteredOptionsByKind = options => {
        return filter(options, ({ kind }) => includes(selectedCompanyGroup, kind));
    };

    const validateFieldRequired = (values) => {
        return isEmpty(get(values, 'monthsEvaluate')) ||
            isEmpty(get(values, 'associatedStore')) ||
            get(pollSelected, 'numberReponses', 0) > 0
    }

    return <Drawer
        title={`${isCreate ? 'Crear' : 'Editar'} encuesta`}
        placement='right'
        onClose={onClose}
        visible={visible}
        width={750}
        destroyOnClose
    >
        <Formik initialValues={isCreate ? {} : initialValues()} onSubmit={submit}>
            {({ values, setFieldValue }) => {
                return <Form style={{ height: '100%', display: 'flex', flexDirection: 'column', marginBottom: 100 }}>
                    {/* Body */}
                    <Tabs defaultActiveKey='1' style={{ width: '100%', padding: '0px 28px 50px 28px' }}>
                        <TabPane tab='Datos' key='1'>
                            <Column gap={5}>
                                <Field component={FormikInput} label='Nombre encuesta' name='namePoll' maxLength={110} style={{ marginTop: 20 }} />
                                <Field component={FormikInput} label='Título' name='title' maxLength={110} />
                                <Field component={FormikInput} label='Mensaje de bienvenida' value='!Tu opinión es muy importante para nosotros!' disabled style={{ marginBottom: 0 }} />
                                <Field component={FormikInput} name='welcomeMessage' textArea maxLength={200} style={{ marginTop: 0 }} />
                                <Field component={FormikSelect} label='Meses a evaluar' name='monthsEvaluate' options={monthOptions} multiple />
                                <CompanyGroupsSelect onChange={({ value }) => handleSelectedCompanyGroup(value, setFieldValue)} initialValue={{ label: defaultFlag || 'Jumbo', value: defaultFlag || 'Jumbo' }} />
                                <Field component={FormikSelect} label='Tienda' name='associatedStore' options={salesChannelFilteredOptionsByKind(sessionSalesChannel)} multiple />
                                <Field component={FormikDatePicker} label='Rango de fecha de activación' name='activationRange' range position='fixed' portalStyle={{ zIndex: 1, marginBottom: 400, top: 440 }} disabledDate={disabledShippingDates} showDateInput={false} />
                            </Column>
                        </TabPane>
                        <TabPane tab='Preguntas' key='2'>
                            <AddQuestionButton values={values} setFieldValue={setFieldValue} />
                            <Questions setFieldValue={setFieldValue} values={values} />
                        </TabPane>
                    </Tabs>
                    {/* Footer */}
                    <ConfirmFooterButtons justify='center' width={200} withZindex htmlType='submit' confirmDisabled={success || validateFieldRequired(values)} onCancel={onClose} loading={loading} />
                </Form>;
            }}
        </Formik>
    </Drawer>;
};

DrawerPoll.propTypes = {
    visible: bool,
    onClose: func,
    pollSelected: object,
    isCreate: bool,
    session: object,
    client: object,
    refreshData: func
};

export default DrawerPoll;
